import React, { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { useTranslation } from 'react-i18next'
import { cardSx } from '../../utils/depth'

const itemTones = {
  default: ['rgba(51,65,85,0.08)', '#334155'],
  primary: ['rgba(244,102,31,0.1)', '#F4661F'],
  blue: ['rgba(47,95,224,0.1)', '#2F5FE0'],
  green: ['rgba(21,128,61,0.1)', '#15803D'],
  danger: ['rgba(220,38,38,0.08)', '#DC2626']
}

/**
 * The "…" menu at the end of a table row. `actions` is a list of
 * { label, icon, onClick, tone, disabled, divider, confirm }; `tone: 'danger'`
 * paints the item red, `divider` draws a rule above it, and `confirm`
 * ({ title, message, confirmLabel, cancelLabel }) asks before running onClick.
 * Danger actions always confirm (with a default message) unless they pass
 * `confirm: false` because they open their own dialog.
 * Clicks are kept from bubbling so rows with onRowClicked don't also fire.
 */
export function RowActionMenu({ actions, label = 'Actions' }) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [pending, setPending] = useState(null)
  const close = () => setAnchorEl(null)
  const open = Boolean(anchorEl)

  return (
    <Box onClick={e => e.stopPropagation()}>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          aria-haspopup="true"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: open ? 'primary.main' : 'divider',
            bgcolor: open ? 'rgba(244,102,31,0.08)' : 'common.white',
            color: open ? 'primary.main' : 'text.secondary',
            transition: 'all .15s ease',
            '&:hover': {
              borderColor: 'grey.400',
              bgcolor: 'grey.50',
              color: 'text.primary'
            }
          }}>
          <MoreHorizRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            ...cardSx,
            mt: 0.75,
            minWidth: 210,
            p: 0.75,
            '& .MuiList-root': { py: 0 }
          }
        }}>
        {actions.filter(Boolean).map(action => {
          const [bg, fg] = itemTones[action.tone] || itemTones.default
          const Icon = action.icon
          return [
            action.divider ? (
              <Box
                key={`${action.label}-divider`}
                sx={{ height: '1px', bgcolor: 'divider', my: 0.5, mx: 1 }}
              />
            ) : null,
            <MenuItem
              key={action.label}
              disabled={action.disabled}
              onClick={() => {
                close()
                const confirm =
                  action.confirm === undefined && action.tone === 'danger'
                    ? {}
                    : action.confirm
                if (confirm) setPending({ ...action, confirm })
                else action.onClick()
              }}
              sx={{
                gap: 1.25,
                borderRadius: '10px',
                minHeight: 40,
                px: 1,
                fontSize: 14,
                fontWeight: 600,
                color: action.tone === 'danger' ? fg : 'text.primary',
                '&:hover': {
                  bgcolor: action.tone === 'danger' ? bg : 'grey.50'
                }
              }}>
              {Icon ? (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: bg,
                    color: fg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                  <Icon sx={{ fontSize: 17 }} />
                </Box>
              ) : null}
              {action.label}
            </MenuItem>
          ]
        })}
      </Menu>
      {pending ? (
        <ConfirmDialog
          open
          title={pending.confirm.title || `${pending.label}?`}
          message={pending.confirm.message || t('DeleteConfirm')}
          confirmLabel={pending.confirm.confirmLabel || pending.label}
          cancelLabel={pending.confirm.cancelLabel || t('Cancel')}
          onClose={() => setPending(null)}
          onConfirm={() => {
            setPending(null)
            pending.onClick()
          }}
        />
      ) : null}
    </Box>
  )
}

/**
 * Centred "are you sure?" dialog for destructive actions. The confirm button
 * shows a spinner while `loading` so it can't be pressed twice.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loading,
  onConfirm,
  onClose
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: { ...cardSx, width: '100%', maxWidth: 420, p: 3, m: 2 }
      }}>
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            bgcolor: 'rgba(220,38,38,0.08)',
            color: '#DC2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <WarningAmberRounded sx={{ fontSize: 30 }} />
        </Box>
        <Typography
          sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>
          {title}
        </Typography>
        {message ? (
          <Typography
            sx={{
              fontSize: 14,
              color: 'text.secondary',
              mt: 1,
              lineHeight: 1.5
            }}>
            {message}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
        <Button
          fullWidth
          disabled={loading}
          onClick={onClose}
          sx={{
            minHeight: 44,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
            '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.400' }
          }}>
          {cancelLabel}
        </Button>
        <Button
          fullWidth
          disabled={loading}
          onClick={onConfirm}
          sx={{
            minHeight: 44,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            color: 'common.white',
            background: 'linear-gradient(145deg, #F87171 0%, #DC2626 100%)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.35), 0 8px 18px -6px rgba(220,38,38,0.5)',
            '&:hover': {
              background: 'linear-gradient(145deg, #F87171 0%, #B91C1C 100%)'
            },
            '&.Mui-disabled': { color: 'common.white', opacity: 0.7 }
          }}>
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'common.white' }} />
          ) : (
            confirmLabel
          )}
        </Button>
      </Box>
    </Dialog>
  )
}

export default RowActionMenu
